import * as vp from 'vopay';
import { Box, Title, Table, TextField, Card, Icon } from 'vopay';
import { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useFetchAccountUsers from '@/hooks/useApi/useFetchAccountUsers';
import userPostDeleteUser from '@/hooks/useApi/usePostDeleteAccountUser';
import DeleteUserContext, { DeleteUserCtx } from './DeleteUser/DeleteUserContext';
import DeleteUserDialogue from './DeleteUser/DeleteUserDialogue';
import { DeleteUserDialogueHandler, DialogueHandlerProps } from './types';
import { usePermissions } from '@/providers/Permissions.provider';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';
import { Button } from 'UI';
import useResetPassword from './useResetPassword';
import AccountUserPermissionsDrawer from './UserPermissions/AccountUserPermissionsDrawer';

type AccountUser = {
  Username: string;
  DateAdded: string;
  ViewButtons: vp.ButtonProps[];
};

const UsersList: FC = () => {
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const { resetPassword } = useResetPassword();
  const { userSettings } = useUserSettingsProvider();
  const [shouldFetchUsers, setFetchUsers] = useState(true);
  const [DeleteUserError, setDeleteUserError] = useState('');
  const [showDeleteUserErrorAlert, setShowDeleteUserErrorAlert] = useState(false);
  const [showDeleteUserSuccessAlert, setShowDeleteUserSuccessAlert] = useState(false);
  const [showDeleteUserConfirmationAlert, setshowDeleteUserConfirmationAlert] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [shouldDeleteUser, setShouldDeleteUser] = useState(false);
  const [DeleteUserResponse, deleteUser] = userPostDeleteUser({});
  const [users, fetchUsers] = useFetchAccountUsers({});
  const [filteredUsers, setFilteredUsers] = useState<vp.AccountUser[]>([]);
  const history = useHistory();
  const [searchTerm, setSearchTerm] = useState('');
  const { permissions } = usePermissions();
  const { t } = useTranslation('translations', { keyPrefix: 'Users.UsersList' });
  const [isPermissionsDrawerOpen, setIsPermissionsDrawerOpen] = useState(false);
  const [permissionsEmail, setPermissionsEmail] = useState<string>();

  useEffect(() => {
    if (!shouldFetchUsers) return;

    fetchUsers({
      Timezone: userSettings.timezone,
    });
  }, [shouldFetchUsers]);
  useEffect(() => {
    const filteredUsersToSet = Object.values(users.Users || {}).filter(
      (user) => searchTerm === '' || user.Username.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    setFilteredUsers(filteredUsersToSet);
  }, [searchTerm, users]);

  const handleResetPasswordDialog = async (userEmail: string) => {
    const result = await alertDialog.open(
      t('ResetPassword.title', { defaultValue: 'Send Password Reset Email?' }),
      t('ResetPassword.description', {
        defaultValue: 'This will send a secure password reset link to the user’s email address.',
      }),
      {
        abortLabel: t('ResetPassword.cancelButton', { defaultValue: 'Cancel Button' }),
        confirmLabel: t('ResetPassword.sendButton', { defaultValue: 'Send Reset Email' }),
      },
    );

    if (result === 'confirm') {
      try {
        await resetPassword({
          Username: userEmail,
        });
        toast.success(t('ResetPassword.success', { defaultValue: 'The link was sent successfully.' }));
      } catch (err) {
        console.error(err);
        toast.neutral(t('ResetPassword.fail', { defaultValue: 'Unable to send the link.' }));
      }
    }
  };

  const tableProps: vp.TableProps<AccountUser> = {
    id: 'users-list',
    version: '2',
    zebra: true,
    columns: [
      { id: 'Username', header: t('table.email') || '', align: 'left', field: 'Username' },
      { id: 'DateAdded', header: t('table.dateAdded') || '', align: 'left', field: 'DateAdded' },
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) => (
          <Box jc="fe" gap="sm">
            {data.ViewButtons.map((btn) => {
              return (
                <Button key={btn.id} size="small" onClick={btn.onClick}>
                  {btn.label}
                </Button>
              );
            })}
          </Box>
        ),
      },
    ],
    rows: filteredUsers.map((user) => {
      const buttons: vp.ButtonProps[] = [];

      if (permissions.ManageUserPermissions) {
        buttons.push({
          label: t('actions.permissions'),
          id: `user-change-Permissions-${user.DateAdded}`,
          onClick: () => {
            setPermissionsEmail(user.Username);
            setIsPermissionsDrawerOpen(true);
          },
        });
      }

      if (permissions.AddUsers) {
        buttons.push({
          label: t('actions.resetPassword', { defaultValue: 'Reset Password' }),
          id: `user-change-${user.DateAdded}`,
          onClick: () => {
            handleResetPasswordDialog(user.Username);
          },
        });
        buttons.push({
          label: t('actions.remove'),
          id: `user-delete-${user.DateAdded}`,
          onClick: () => {
            setEmailAddress(user.Username);
            setshowDeleteUserConfirmationAlert(true);
          },
        });
      }

      return {
        id: user.Username,
        data: {
          Username: user.Username,
          DateAdded: user.DateAdded,
          ViewButtons: buttons,
        },
      };
    }),
  };

  const DeleteUser: DeleteUserCtx = {
    showDeleteUserErrorAlert,
    showDeleteUserSuccessAlert,
    showDeleteUserConfirmationAlert,
    DeleteUserError,
  };

  useEffect(() => {
    if (shouldDeleteUser) {
      setShouldDeleteUser(false);
      const { Success, ErrorMessage } = DeleteUserResponse;
      if (Success === true) {
        setShowDeleteUserSuccessAlert(true);
      } else {
        setDeleteUserError(ErrorMessage);
        setShowDeleteUserErrorAlert(true);
      }
    }
  }, [DeleteUserResponse]);

  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<DeleteUserDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: DeleteUserDialogueHandler = {
        delete: {
          success: {
            default: () => {
              setShowDeleteUserSuccessAlert(false);
              setFetchUsers(true);
              fetchUsers({
                Timezone: userSettings.timezone,
              });
            },
          },
          fail: {
            default: () => {
              setShowDeleteUserErrorAlert(false);
              setDeleteUserError('');
            },
          },
        },
        alertDelete: {
          success: {
            default: () => {
              setshowDeleteUserConfirmationAlert(false);

              const DeleteUserRequest = {
                EmailAddress: emailAddress,
              };
              setShouldDeleteUser(true);
              deleteUser(DeleteUserRequest);
            },
          },
          fail: {
            default: () => {
              setshowDeleteUserConfirmationAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
  };

  return (
    <div>
      <DeleteUserContext.Provider value={DeleteUser}>
        <Box mb="md" jc="sb">
          <Title title={t('title')} />
          <Box gapSize="16px" ai="center">
            <TextField
              key="UserSearchKeyword"
              name="UserSearchKeyword"
              placeholder={t('search.placeholder')}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
              variant="outlineShadow"
              elementLeft={
                <div style={{ marginLeft: '14px' }}>
                  <Icon icon="search" width="17px" height="17px" color="none" />
                </div>
              }
            />
            {permissions.AddUsers && (
              <Button
                id="showHideAccountCredentials"
                onClick={() => {
                  history.push('/users/create');
                }}
                colorScheme="primary"
              >
                {t('actions.addUser')}
              </Button>
            )}
          </Box>
        </Box>
        <Card
          style={{
            padding: '16px',
          }}
        >
          <Table {...tableProps} />
        </Card>
        <DeleteUserDialogue handleDialogue={handlers.handleDialogue} />
      </DeleteUserContext.Provider>
      <AccountUserPermissionsDrawer
        isOpen={isPermissionsDrawerOpen}
        onClose={() => setIsPermissionsDrawerOpen(false)}
        emailAddress={permissionsEmail}
      />
    </div>
  );
};
export default UsersList;
